import React, { memo, useCallback } from "react";
import { useQuantityTypesTable } from "@/pages-components/products/digital-offset-price/widgets/render-parameter-widgets/quantity-parameter/table/use-quantity-types-table";
import { GomakeTextInput } from "@/components";
import { ChangeEvent } from "react";
import { IQuantityTypesValue } from "@/pages-components/products/digital-offset-price/widgets/render-parameter-widgets/quantity-parameter/quantity-types/state";
import { Box, IconButton, Stack } from "@mui/material";
import { FixedSizeList as List } from "react-window";
import { useTranslation } from "react-i18next";
import { TrashIcon } from "@/components/icons/trash-icon";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

const LIST_HEIGHT = 295;
const ROW_HEIGHT = 56;
const DELETE_COL_WIDTH = 36;
const SCROLLBAR_GUTTER = 12;
const GRID_COLUMNS = `minmax(0, 1fr) minmax(0, 1fr) ${DELETE_COL_WIDTH}px`;

interface IQuantityTypesTableProps {
  save: boolean;
  onSave: (v: IQuantityTypesValue[]) => void;
  onDeleteQuantityType: (index: number) => void;
}

interface RowProps {
  index: number;
  style: React.CSSProperties;
  data: {
    values: IQuantityTypesValue[];
    onQuantityTypeChange: (index: number, key: "quantity" | "name", value: string | number) => void;
    onDeleteQuantityType: (index: number) => void;
    errorColor: (weight: number) => string;
    dir: "rtl" | "ltr";
  };
}

const Row = memo(({ index, style, data }: RowProps) => {
  const { values, onQuantityTypeChange, onDeleteQuantityType, errorColor, dir } = data;
  const value = values[index];
  const { t } = useTranslation();

  const handleNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onQuantityTypeChange(index, "name", event.target.value);
    },
    [index, onQuantityTypeChange]
  );

  const handleQuantityChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onQuantityTypeChange(index, "quantity", +event.target.value);
    },
    [index, onQuantityTypeChange]
  );

  const handleDelete = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDeleteQuantityType(index);
    },
    [index, onDeleteQuantityType]
  );

  if (!value) {
    return null;
  }

  return (
    <div
      dir={dir}
      style={{
        ...style,
        display: "grid",
        gridTemplateColumns: GRID_COLUMNS,
        alignItems: "center",
        columnGap: 8,
        paddingInlineStart: 8,
        paddingInlineEnd: 4,
        boxSizing: "border-box",
        left: dir === "rtl" ? undefined : 0,
        right: dir === "rtl" ? 0 : undefined,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <GomakeTextInput
          onChange={handleNameChange}
          style={{ height: "40px", width: "100%" }}
          value={value.name}
          direction={dir}
        />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <GomakeTextInput
          onChange={handleQuantityChange}
          style={{ height: "40px", width: "100%" }}
          value={value.quantity}
          direction={dir}
        />
      </Box>
      <IconButton
        size="small"
        onClick={handleDelete}
        aria-label={t("quantityTypes.deleteType")}
        sx={{
          flexShrink: 0,
          width: DELETE_COL_WIDTH,
          height: DELETE_COL_WIDTH,
          padding: 0,
        }}
      >
        <TrashIcon width={18} height={18} color={errorColor(500)} />
      </IconButton>
    </div>
  );
});

const QuantityTypesTable = ({ save, onDeleteQuantityType }: IQuantityTypesTableProps) => {
  const { values, onQuantityTypeChange } = useQuantityTypesTable(save);
  const { t, i18n } = useTranslation();
  const dir: "rtl" | "ltr" = i18n.dir() === "rtl" ? "rtl" : "ltr";
  const { errorColor } = useGomakeTheme();
  const itemData = {
    values,
    onQuantityTypeChange,
    onDeleteQuantityType,
    errorColor,
    dir,
  };

  return (
    <Stack dir={dir} width="100%" overflow="hidden">
      <Box
        dir={dir}
        sx={{
          display: "grid",
          gridTemplateColumns: GRID_COLUMNS,
          alignItems: "center",
          columnGap: "8px",
          backgroundColor: "#EAECF0",
          borderRadius: "8px 8px 0 0",
          paddingInlineStart: "8px",
          paddingInlineEnd: `${4 + SCROLLBAR_GUTTER}px`,
          minHeight: 40,
        }}
      >
        <Box sx={{ textAlign: "center", fontWeight: 500, fontSize: 13 }}>
          {t("quantityTypes.typeName")}
        </Box>
        <Box sx={{ textAlign: "center", fontWeight: 500, fontSize: 13 }}>
          {t("quantityTypes.quantity")}
        </Box>
        <Box sx={{ width: DELETE_COL_WIDTH }} />
      </Box>

      <Box
        className="quantity-types-virtual-list-wrap"
        dir={dir}
        sx={{
          width: "100%",
          border: "1px solid #EAECF0",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
          overflow: "hidden",
        }}
      >
        <List
          className="quantity-types-virtual-list"
          direction={dir}
          height={LIST_HEIGHT}
          width="100%"
          itemCount={values.length}
          itemSize={ROW_HEIGHT}
          itemData={itemData}
        >
          {Row}
        </List>
      </Box>

      <style>{`
        .quantity-types-virtual-list {
          overflow-x: hidden !important;
          scrollbar-gutter: stable;
          scrollbar-width: thin;
          scrollbar-color: #cccdcf #f1f1f1;
        }
        .quantity-types-virtual-list::-webkit-scrollbar {
          width: 8px;
        }
        .quantity-types-virtual-list::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 8px;
        }
        .quantity-types-virtual-list::-webkit-scrollbar-thumb {
          background: #cccdcf;
          border-radius: 8px;
        }
        .quantity-types-virtual-list::-webkit-scrollbar-thumb:hover {
          background: #b0b0b0;
        }
      `}</style>
    </Stack>
  );
};

export { QuantityTypesTable };
